import { ANON_ROLE_UID, NON_BILLABLE_FLUX_USER_UIDS } from "@buildwithflux/constants";
import { allFromSnapshot, allFromSnapshotByUid, metadataStrategyOverwrite, metadataStrategySpecify, oneFromSnapshot, withBatchingForConnection, } from "@buildwithflux/firestore-compatibility-layer";
import { DataModelSchemaVersion, documentSubcollectionFields, intoBaseDocument, PermissionType, ProfileDocumentType, reKeyDocumentConfigs, userPermissionsToPermissionsMap, } from "@buildwithflux/models";
import { getObjectSizeInBytes, hasBrowserWindowAccess, isDevEnv, silentLogger } from "@buildwithflux/shared";
import { diff } from "deep-diff";
import { difference, intersection, isEqual, keyBy, mapKeys, without } from "lodash";
import { DocumentDataBuilder } from "./DocumentDataBuilder";
import { quotaForOperationOnSubcollection } from "./quota";
import { DefaultDocumentRepositoryStrategy, UserContextDocumentRepositoryStrategy, } from "./strategy";
export class FirestoreDocumentRepository {
    db;
    functions;
    logger;
    overrideStrategy;
    constructor(db, functions, logger = silentLogger, overrideStrategy = undefined) {
        this.db = db;
        this.functions = functions;
        this.logger = logger;
        this.overrideStrategy = overrideStrategy;
    }
    async getDataForCurrentUser(documentUid, currentUserUid) {
        const strategy = this.getStrategy("userContext", currentUserUid);
        const documentData = (await this.getHydrated(documentUid, strategy))?.documentData;
        return documentData;
    }
    /** @inheritDoc */
    getBaseWithoutMigrations(documentUid) {
        return this.getBase(documentUid, this.getStrategy("withoutMigrations"));
    }
    /** @inheritDoc */
    getBaseDataWithoutMigrations(documentUid) {
        return this.getBaseData(documentUid, this.getStrategy("withoutMigrations"));
    }
    /** @inheritDoc */
    async getDataWithoutMigrations(documentUid) {
        return (await this.getHydrated(documentUid, this.getStrategy("withoutMigrations")))?.documentData;
    }
    /** @inheritDoc */
    getWithoutMigrations(documentUid) {
        return this.getHydrated(documentUid, this.getStrategy("withoutMigrations"));
    }
    /** @inheritDoc */
    async saveLayerViewConfig(documentUid, layerViewConfig) {
        const baseRef = this.db.document(documentUid);
        this.logger.debug(`FirestoreDocumentRepository: saving documentData.layerViewConfig`);
        await baseRef.update("documentData.layerViewConfig", layerViewConfig);
    }
    /** @inheritDoc */
    async save(document, collectionsToSave = documentSubcollectionFields, previousDocument, saveBaseDocument = true, metadataStrategy = metadataStrategyOverwrite("baseDocument")) {
        const documentUid = document.uid;
        const baseRef = this.db.document(documentUid, metadataStrategy);
        if (saveBaseDocument) {
            const documentData = intoBaseDocument(document);
            if (!previousDocument) {
                this.logger.debug("FirestoreDocumentRepository: Saving base document, because no previous version of it found", {
                    documentUid,
                    diffCache: "miss",
                });
                await baseRef.set({ documentData }, { merge: true }).then();
            }
            else {
                const previousDocumentData = intoBaseDocument(previousDocument);
                if (!isEqual(previousDocumentData, documentData)) {
                    this.logger.debug("FirestoreDocumentRepository: Saving base document, because difference found between previous document and document being saved", {
                        documentUid,
                        diffCache: "partial-hit",
                        diff: isDevEnv() ? diff(previousDocumentData, documentData) : "dev-only",
                    });
                    await baseRef.set({ documentData }, { merge: true }).then();
                }
                else {
                    this.logger.debug("Skipping base document save: no changes", {
                        documentUid,
                        diffCache: "hit",
                    });
                }
            }
        }
        // Then write any roles
        if (collectionsToSave.includes("roles")) {
            await this.conditionallyWriteCollection(document, "roles", previousDocument);
        }
        // Then the rest of the collections, except for comment_threads and userUiStates which are managed out-of-band
        await Promise.all(without(collectionsToSave, "roles", "comment_threads", "userUiStates").map((collection) => {
            return this.conditionallyWriteCollection(document, collection, previousDocument);
        }));
    }
    async saveWithMetadata(document) {
        await this.save(document.documentData, documentSubcollectionFields, undefined, true, metadataStrategySpecify("baseDocument", document.metadata));
    }
    async update(documentUid, fields) {
        const updates = mapKeys(fields, (_value, field) => `documentData.${field}`);
        return this.db.documentCollection().doc(documentUid).update(updates).then();
    }
    async getBaseChunk({ uids, accessListForQuery, includeArchived = false, documentTypeFilter = ProfileDocumentType.all, }) {
        const documents = (await Promise.allSettled(uids.map((uid) => this.getStrategy("withoutMigrations").getFromFirestore(this.db.documentCollection().doc(uid))))).filter((result, i) => {
            if (result.status !== "fulfilled") {
                this.logger.error("FirestoreDocumentRepository: failed to get document", {
                    documentUid: uids[i],
                    error: result.reason,
                });
                return false;
            }
            const doc = result.value;
            return (doc.exists &&
                (includeArchived || !doc.data()?.documentData?.archived) &&
                (documentTypeFilter === ProfileDocumentType.all
                    ? true
                    : documentTypeFilter === ProfileDocumentType.project
                        ? doc.data()?.documentData?.belongs_to_part_uid === ""
                        : doc.data()?.documentData?.belongs_to_part_uid !== "") &&
                intersection(doc.data()?.documentData?.user_access_list ?? [], accessListForQuery).length > 0);
        });
        return documents.map((doc) => doc.value.data().documentData);
    }
    /** @inheritDoc */
    async migrate(documentUid, targetVersion) {
        targetVersion = targetVersion ?? DataModelSchemaVersion; // TODO remove this testing parameter once Bryan's childrenUids migration has been run in the wild for some time.
        await this.functions.schemaVersion({ documentUid, targetVersion });
    }
    /** @inheritDoc */
    async getBaseDataByUid(documentUid) {
        return oneFromSnapshot(await this.getStrategy("withoutMigrations").getFromFirestore(this.db.document(documentUid)), () => undefined)?.documentData;
    }
    /**
     * @inheritDoc
     */
    async getAllBaseDataByPublicStarCount(minimumStarCount = 2) {
        return allFromSnapshot(await this.getStrategy("withoutMigrations").getFromFirestore(this.db
            .documentCollection()
            .where("documentData.archived", "==", false)
            .where("documentData.user_access_list", "array-contains", ANON_ROLE_UID)
            .where("documentData.star_count", ">=", minimumStarCount))).map((document) => document.documentData);
    }
    /** @inheritdoc */
    async getProperties(documentUid, propertyNames) {
        const propertiesCollection = this.db.documentPropertiesCollection(documentUid);
        const propertiesDocs = (await this.getStrategy("withoutMigrations").getFromFirestore(propertiesCollection.where("data.name", "in", propertyNames))).docs;
        const propertiesByName = keyBy(propertiesDocs.map((doc) => doc.data()), "name");
        return propertyNames.reduce((acc, name) => {
            const propertyData = propertiesByName[name] ?? null;
            acc[name] = propertyData;
            return acc;
        }, {});
    }
    /** @inheritdoc */
    async getAllProperties(documentUid) {
        const propertiesCollection = this.db.documentPropertiesCollection(documentUid);
        return allFromSnapshotByUid(await this.getStrategy("withoutMigrations").getFromFirestore(propertiesCollection));
    }
    /** @inheritdoc */
    async getRoles(documentUid) {
        return userPermissionsToPermissionsMap(allFromSnapshot(await this.getStrategy("withoutMigrations").getFromFirestore(this.db.documentRolesCollection(documentUid))));
    }
    async *getAllEditorsForPrivateOrganizationDocs(organizationUid) {
        const pageSize = 100;
        const nonBillableUserUids = new Set(NON_BILLABLE_FLUX_USER_UIDS);
        for await (const document of this.getAllNonPubliclyAccessibleDocumentsForOrganization(organizationUid)) {
            yield document.documentData.owner_uid;
            let query = this.db
                .documentRolesCollection(document.documentData.uid)
                .where("data.permission_type", "==", PermissionType.edit)
                .where("data.user_uid", "!=", ANON_ROLE_UID)
                .orderBy("data.user_uid", "asc") // else INVALID_ARGUMENT: inequality filter property and first sort order must be the same: data.user_uid and __key__
                .limit(pageSize);
            let lastRole = null;
            while (true) {
                if (lastRole) {
                    query = query.startAfter(lastRole);
                }
                const snapshot = await this.getStrategy("withoutMigrations").getFromFirestore(query);
                if (snapshot.empty) {
                    break;
                }
                for (const role of allFromSnapshot(snapshot)) {
                    if (!nonBillableUserUids.has(role.user_uid)) {
                        yield role.user_uid;
                    }
                }
                lastRole = snapshot.docs[snapshot.docs.length - 1] ?? null;
            }
        }
    }
    /** @inheritDoc */
    async hasAtLeastOneOrganizationPrivateDocument(organizationUid) {
        const query = this.db
            .documentCollection()
            .where("documentData.organization_owner_uid", "==", organizationUid)
            .where("documentData.archived", "==", false)
            .where("documentData.has_anon_access", "==", false)
            .limit(1);
        return this.getStrategy("withoutMigrations")
            .getFromFirestore(query)
            .then((snapshot) => snapshot.size > 0);
    }
    /** @inheritDoc */
    async hasAtLeastOneOrganizationPrivateDocumentEditableByMembers(organizationUid) {
        const query = this.db
            .documentCollection()
            .where("documentData.archived", "==", false)
            .where("documentData.organization_owner_uid", "==", organizationUid)
            .where("documentData.has_anon_access", "==", false)
            .where("documentData.organization_member_permission_type", "==", PermissionType.edit)
            .limit(1);
        return this.getStrategy("withoutMigrations")
            .getFromFirestore(query)
            .then((snapshot) => snapshot.size > 0);
    }
    /** @inheritDoc */
    async getConfigs(baseDocumentData) {
        const strategy = this.getStrategy("userContext");
        const configDatas = allFromSnapshot(await strategy.getFromFirestore(strategy.decorateConfigsCollection(this.db.documentConfigsCollection(baseDocumentData.uid), baseDocumentData)));
        return reKeyDocumentConfigs(configDatas);
    }
    subscribeToLatest(documentUid, onChange) {
        let previousActionRecordUid;
        return this.db.document(documentUid).onSnapshot((doc) => {
            const documentData = doc.data()?.documentData;
            const latestUid = documentData?.latestActionRecordUid;
            if (latestUid && latestUid !== previousActionRecordUid) {
                previousActionRecordUid = latestUid;
                onChange(documentData);
            }
        });
    }
    getStrategy(strategy, currentUserUid = undefined) {
        if (this.overrideStrategy) {
            return this.overrideStrategy;
        }
        switch (strategy) {
            case "withoutMigrations":
                return new DefaultDocumentRepositoryStrategy();
            case "userContext":
                return new UserContextDocumentRepositoryStrategy(this.functions, currentUserUid);
            default:
                throw new Error(`Unknown strategy: ${strategy}`);
        }
    }
    async *getAllNonPubliclyAccessibleDocumentsForOrganization(organizationUid) {
        const pageSize = 100;
        let query = this.db
            .documentCollection()
            .where("documentData.archived", "==", false)
            .where("documentData.organization_owner_uid", "==", organizationUid)
            .where("documentData.has_anon_access", "==", false)
            .limit(pageSize);
        let lastDocument = null;
        while (true) {
            if (lastDocument) {
                query = query.startAfter(lastDocument);
            }
            const snapshot = await this.getStrategy("withoutMigrations").getFromFirestore(query);
            if (snapshot.empty) {
                break;
            }
            yield* allFromSnapshot(snapshot);
            lastDocument = snapshot.docs[snapshot.docs.length - 1] ?? null;
        }
    }
    /**
     * Writes a collection, with reference to a previously written collection
     */
    async conditionallyWriteCollection(document, field, previousDocument) {
        const documentUid = document.uid;
        const newFieldValues = document[field];
        if (newFieldValues === undefined) {
            // We don't write undefined fields in case they were unhydrated
            return;
        }
        const collectionForField = this.db.documentSubcollectionForField(documentUid, field);
        if (!previousDocument || !previousDocument[field]) {
            // Nothing to diff against, so just write the whole collection
            // This is usually the case for e.g. backend code that doesn't track writes to do this diffing
            return withBatchingForConnection(this.db, this.logger, async (addToBatch) => {
                const entries = Object.entries(newFieldValues);
                for (const [id, object] of entries) {
                    addToBatch((batch) => {
                        batch.set(collectionForField.doc(id), object);
                        return quotaForOperationOnSubcollection(field);
                    });
                }
                if (entries.length > 0) {
                    this.logger.debug(`FirestoreDocumentRepository: enqueued ${entries.length} writes to ${field}`, {
                        field,
                        documentUid,
                        diffCache: "miss",
                        updated: entries.length,
                    });
                }
            });
        }
        const oldFieldValues = previousDocument[field] || {};
        // In this case, we have some previous and current collection of objects, keyed by UID
        const current = Object.keys(newFieldValues);
        const previous = Object.keys(oldFieldValues);
        await withBatchingForConnection(this.db, this.logger, (addToBatch) => {
            const stats = { created: 0, deleted: 0, updated: 0, skipped: 0 };
            for (const id of difference(previous, current)) {
                stats.deleted++;
                addToBatch((batch) => {
                    batch.delete(this.db
                        .documentSubcollectionForField(documentUid, field)
                        .doc(id));
                    return quotaForOperationOnSubcollection(field);
                });
            }
            for (const id of difference(current, previous)) {
                stats.created++;
                // `as any` here because TS doesn't seem to be able to handle the generics
                // we're throwing at batch.set below otherwise!
                const newValue = newFieldValues[id];
                addToBatch((batch) => {
                    batch.set(this.db.documentSubcollectionForField(documentUid, field).doc(id), newValue);
                    return quotaForOperationOnSubcollection(field);
                });
            }
            for (const id of intersection(current, previous)) {
                // `as any` here because TS doesn't seem to be able to handle the generics
                // we're throwing at batch.set below otherwise!
                const oldValue = oldFieldValues[id];
                const newValue = newFieldValues[id];
                // NOTE: in the common case, oldValue should be equal to
                // newValue by reference (===) for best performance
                if (!isEqual(oldValue, newValue)) {
                    stats.updated++;
                    addToBatch((batch) => {
                        batch.set(this.db.documentSubcollectionForField(documentUid, field).doc(id), newValue);
                        return quotaForOperationOnSubcollection(field);
                    });
                }
                else {
                    stats.skipped++;
                }
            }
            const total = stats.created + stats.deleted + stats.updated;
            if (total > 0) {
                this.logger.debug(`FirestoreDocumentRepository: enqueued ${total} writes to ${field}`, {
                    field,
                    documentUid,
                    diffCache: "hit",
                    updated: stats.created + stats.updated, // To match the definition in the "miss" case
                    deleted: stats.deleted,
                });
            }
        });
    }
    async getBase(documentUid, strategy) {
        return await strategy.decorateBaseDocument(this.db.document(documentUid));
    }
    async getBaseData(documentUid, strategy) {
        return (await this.getBase(documentUid, strategy))?.documentData;
    }
    /**
     * Private implementation of a fully hydrating get operation for the document
     *
     * As well as the base document, loads documents' subcollections and return them attached to the document
     */
    async getHydrated(documentUid, strategy) {
        const baseDoc = await this.getBase(documentUid, strategy);
        if (baseDoc) {
            // noinspection ES6MissingAwait
            const subCollections = [
                strategy.getFromFirestore(this.db.documentActiveUsersCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentAssetsCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentCommentThreadsCollection(documentUid)),
                strategy.getFromFirestore(strategy.decorateConfigsCollection(this.db.documentConfigsCollection(documentUid), baseDoc.documentData)),
                strategy.getFromFirestore(this.db.documentElementsCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentNetsCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentPcbLayoutNodesCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentPcbLayoutRuleSetsCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentPropertiesCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentRolesCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentRouteVerticesCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentRoutesCollection(documentUid)),
                strategy.getFromFirestore(this.db.documentInvitesCollection(documentUid)),
                // Question: do we need to load this here? It feels like we are loading this here just so the document data
                // loaded in frontend is complete (i.e. does contain this field)?
                strategy.getFromFirestore(this.db.documentUserUiStatesCollection(documentUid)),
            ];
            const subs = await Promise.all(subCollections);
            const builder = new DocumentDataBuilder(baseDoc.documentData);
            if (subs[0].docs.length > 0)
                builder.withActiveUsers(subs[0].docs.map((doc) => doc.data()));
            if (subs[1].docs.length > 0)
                builder.withAssets(subs[1].docs.map((doc) => doc.data()));
            if (subs[2].docs.length > 0)
                builder.withCommentThreads(subs[2].docs.map((doc) => doc.data()));
            if (subs[3].docs.length > 0)
                builder.withConfigs(subs[3].docs.map((doc) => doc.data()));
            if (subs[4].docs.length > 0) {
                const elementsList = subs[4].docs.map((doc) => doc.data());
                /**
                 * Benchmark to show loaded (decompressed) data size for all elements.
                 *
                 * This is NOT the element size we loaded from firebase because it is decompressed, but this
                 * IS the memory comsumpiton to hold all element data
                 */
                if (isDevEnv() && hasBrowserWindowAccess()) {
                    this.logger.debug(`FirestoreDocumentRepository: loaded ${elementsList.length} elements to memory with size %s KB`, getObjectSizeInBytes(elementsList));
                }
                builder.withElements(elementsList);
            }
            if (subs[5].docs.length > 0)
                builder.withNets(subs[5].docs.map((doc) => doc.data()));
            // NOTE: unlike other subcollections, we want to process an empty array here to add the root node
            if (subs[6].docs)
                builder.withPcbLayoutNodes(subs[6].docs.map((doc) => doc.data()));
            if (subs[7].docs.length > 0)
                builder.withPcbLayoutRuleSets(subs[7].docs.map((doc) => doc.data()));
            if (subs[8].docs.length > 0)
                builder.withProperties(subs[8].docs.map((doc) => doc.data()));
            if (subs[9].docs.length > 0)
                builder.withRoles(subs[9].docs.map((doc) => doc.data()));
            if (subs[10].docs.length > 0)
                builder.withRouteVertices(subs[10].docs.map((doc) => doc.data()));
            if (subs[11].docs.length > 0)
                builder.withRoutes(subs[11].docs.map((doc) => doc.data()));
            if (subs[12].docs.length > 0)
                builder.withInvites(subs[12].docs.map((doc) => doc.data()));
            if (subs[13].docs.length > 0)
                builder.withUserUiStates(subs[13].docs.map((doc) => doc.data()));
            return {
                documentData: builder.build(),
                metadata: baseDoc.metadata,
            };
        }
        return undefined;
    }
}
//# sourceMappingURL=FirestoreDocumentRepository.js.map